// FriendsPage.js
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Navigation from '../components/Navigation';

import { Icon } from '@iconify/react';
import { useUserData } from '../contexts/UserDataContext';
import { TelegramUserContext } from '../App';
import { APP_CONFIG } from '../config/app';
import { getReferredUsers } from '../services/appwrite';

// ----------------------------
// CONFIGURATION
// ----------------------------

const SOCIAL_ICONS = [
  {
    id: 'whatsapp',
    name: 'WhatsApp',
    icon: 'ic:outline-whatsapp',
    bg: '#25D366'
  },
  {
    id: 'messenger',
    name: 'Messenger',
    icon: 'ri:messenger-fill',
    bg: 'linear-gradient(135deg, #00C6FF 0%, #7D5FFF 50%, #FF2D95 100%)'
  },
  {
    id: 'discord',
    name: 'Discord',
    icon: 'skill-icons:discord',
    bg: '#5865F2'
  },
  {
    id: 'x',
    name: 'X (Twitter)',
    icon: 'prime:twitter',
    bg: '#000000'
  },
  {
    id: 'link',
    name: 'Link',
    icon: 'fluent:link-24-regular',
    bg: '#7B7B7B'
  }
];

const INVITE_ITEMS = [
  {
    id: 'telegram',
    title: 'Invite friends',
    reward: `+${APP_CONFIG.REFERRAL_BONUS} $${APP_CONFIG.PRIMARY_COIN_SYMBOL}`,
    icon: 'logos:telegram',
    type: 'icon'
  },
  {
    id: 'premium',
    title: 'Invite premium friends',
    reward: `+${APP_CONFIG.REFERRAL_BONUS * 2} $${APP_CONFIG.PRIMARY_COIN_SYMBOL}`,
    icon: 'material-symbols:star-rate',
    bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FF6B6B 100%)',
    type: 'icon'
  }
];

// ----------------------------
// STYLES (MUST BE DEFINED BEFORE COMPONENT)
// ----------------------------

const Container = styled.div`
  font-size: 13px;
  background: #ffffff;
  height: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  margin: 0;
  padding: 0;
  /* Padding is handled by Header and Navigation components */

  @media (max-width: 375px) {
    font-size: 12px;
  }
`;

const HeaderWrapper = styled.div`
  flex-shrink: 0;
`;

const ScrollableContent = styled.div`
  flex: 1;
  min-height: 0; /* Fix for flexbox overflow issues */
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  -webkit-overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  width: 100%;
  padding-bottom: calc(61px + var(--tg-safe-area-inset-bottom, 0px)); /* Space for fixed navigation */
`;

const FooterWrapper = styled.div`
  flex-shrink: 0;
  width: 100%;
  background: white;
`;

const FriendsContainer = styled.div`
  margin: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const InviteItem = styled.div`
  display: flex;
  align-items: center;
  padding: 16px;

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const IconBox = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  min-height: 50px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
`;

const TextContent = styled.div`
  flex: 1;
`;

const InviteTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
`;

const RewardText = styled.div`
  font-size: 14px;
  color: #666;
`;

const ShareButton = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 12px;
  padding: 12px 0;
  width: 80%;
  margin: 16px auto;
  font-weight: bold;
  cursor: pointer;
  display: block;
`;

const SocialIcons = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 16px;
  padding: 0 4px;
  flex-wrap: nowrap;
  overflow-x: auto;
  
  /* Hide scrollbar but keep functionality */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SocialIcon = styled.button`
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.2s ease, opacity 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
    color: white;
  }

  &:hover {
    opacity: 0.8;
    transform: scale(0.95);
  }
  
  &:active {
    transform: scale(0.9);
  }
`;

const AffiliateSection = styled.div`
  background: white;
  margin: 16px;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const AffiliateTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 8px;
`;

const AffiliateDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
`;

const AmountDisplay = styled.div`
  font-size: 40px;
  font-weight: bold;
  margin: 16px 0;
`;

const WithdrawButton = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: 20px;
  padding: 12px 0;
  width: 80%;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
`;

const CommissionLink = styled.a`
  color: #666;
  font-size: 14px;
  text-decoration: underline;
  display: block;
  margin-top: 16px;
`;

const MyFriendsSection = styled.div`
  background: white;
  margin: 16px;
  border-radius: 12px;
  padding: 16px;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const FriendList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FriendItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f7f7f7;
  border-radius: 8px;
  padding: 12px;
  margin-top: 8px;
  color: #333;
`;

const FriendName = styled.span`
  font-weight: 500;
`;

const JoinDate = styled.span`
  color: #a0a0a0;
  font-size: 14px;
`;

const NoFriendsMessage = styled.p`
  color: #a0a0a0;
  text-align: center;
  margin-top: 20px;
`;

const LoadingSpinner = styled.div`
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
    margin: 20px auto;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

// ----------------------------
// COMPONENT
// ----------------------------

const FriendsPage = ({ onNavigate, isActive }) => { // Added isActive prop
  const { userData, referralStats } = useUserData(); // Get referralStats from context
  const telegramUser = useContext(TelegramUserContext);
  const [friends, setFriends] = useState([]); // Still used for the detailed list
  const [loading, setLoading] = useState(false); // Initialize loading to false, set true only when fetching

  useEffect(() => {
    const fetchFriends = async () => {
      if (!isActive || !telegramUser?.id) { // Check isActive here
        // If page is not active or no user ID, don't fetch.
        // If it was previously active and had friends, keep them, otherwise ensure list is clear if needed.
        // For simplicity, we won't clear `friends` here if page becomes inactive,
        // assuming data staleness isn't a major issue for this brief inactive period.
        return;
      }

      setLoading(true); // Set loading true only when we are about to fetch
      try {
        const referredUsers = await getReferredUsers(telegramUser.id);
        setFriends(referredUsers);
      } catch (error) {
        console.error("Failed to fetch friends:", error);
        setFriends([]); // Clear friends on error to avoid showing stale data
      } finally {
        setLoading(false);
      }
    };

    fetchFriends();
  }, [telegramUser, isActive]); // Added isActive to dependency array

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toISOString().split('T')[0];
  };

  // Handle social sharing
  const handleSocialShare = (platform) => {
    const referralCode = userData?.referral_code || telegramUser?.id;
    if (!referralCode) return;

    const referralUrl = APP_CONFIG.generateReferralUrl(referralCode);
    const shareText = `Join me in ${APP_CONFIG.APP_NAME} and earn ${APP_CONFIG.REFERRAL_BONUS} ${APP_CONFIG.PRIMARY_COIN_SYMBOL}!`;

    switch (platform) {
      case 'whatsapp':
        window.location.href = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + referralUrl)}`;
        break;
      case 'messenger':
        window.location.href = `https://www.facebook.com/dialog/send?link=${encodeURIComponent(referralUrl)}&app_id=your_app_id&redirect_uri=${encodeURIComponent(window.location.href)}`;
        break;
      case 'discord':
        const discordMessage = `${shareText} ${referralUrl}`;
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(discordMessage);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = discordMessage;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.showAlert('Message copied! Now open Discord and paste it in your server or DM.');
        } else {
          alert('Message copied! Now open Discord and paste it in your server or DM.');
        }
        break;
      case 'x':
        window.location.href = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(referralUrl)}`;
        break;
      case 'link':
        // Copy referral link to clipboard
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(referralUrl);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = referralUrl;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        if (window.Telegram && window.Telegram.WebApp) {
          window.Telegram.WebApp.showAlert('Referral link copied to clipboard!');
        } else {
          alert('Referral link copied to clipboard!');
        }
        break;
      default:
        break;
    }
  };

  return (
    <Container>
      <HeaderWrapper>
        <Header secondaryCoinBalance={userData?.balance || 0} primaryCoinBalance={userData?.primary_coin_balance || 0} />
      </HeaderWrapper>

      <ScrollableContent>
        <FriendsContainer>
          {INVITE_ITEMS.map(item => (
            <InviteItem key={item.id}>
              <IconBox style={{ background: item.bg || 'transparent' }}>
                <Icon icon={item.icon} color="#fff" width="100%" height="100%" />
              </IconBox>
              <TextContent>
                <InviteTitle>{item.title}</InviteTitle>
                <RewardText>{item.reward}</RewardText>
              </TextContent>
            </InviteItem>
          ))}

          <ShareButton>Share with friends</ShareButton>

          <SocialIcons>
            {SOCIAL_ICONS.map(({ id, name, icon, bg, size }) => (
              <SocialIcon 
                key={id} 
                title={name} 
                style={{ background: bg }}
                onClick={() => handleSocialShare(id)}
              >
                <Icon icon={icon} width={size || '24px'} height={size || '24px'} />
              </SocialIcon>
            ))}
          </SocialIcons>
        </FriendsContainer>

        <AffiliateSection>
          <AffiliateTitle>Affiliate programs</AffiliateTitle>
          <AffiliateDescription>
            Invite friends to join the mining app and earn rewards.
          </AffiliateDescription>
          <AmountDisplay>$0</AmountDisplay>
          <WithdrawButton onClick={() => onNavigate('wallet')}>Withdrawal</WithdrawButton>
          <CommissionLink href="#">Commission details</CommissionLink>
        </AffiliateSection>

        <MyFriendsSection>
          {/* Use referralStats.referredCount for the count if available */}
          <AffiliateTitle>My Friends ({referralStats?.referredCount !== undefined ? referralStats.referredCount : friends.length})</AffiliateTitle>
          {loading ? (
            <LoadingSpinner />
          ) : friends.length > 0 ? (
            <FriendList>
              {friends.map(friend => (
                <FriendItem key={friend.$id}>
                  <FriendName>{friend.username || 'Anonymous'}</FriendName>
                  <JoinDate>{formatDate(friend.$createdAt)}</JoinDate>
                </FriendItem>
              ))}
            </FriendList>
          ) : (
            <NoFriendsMessage>You haven't referred any friends yet.</NoFriendsMessage>
          )}
        </MyFriendsSection>
      </ScrollableContent>

      <FooterWrapper>
        <Navigation activePage="friends" onNavigate={onNavigate} />
      </FooterWrapper>
    </Container>
  );
};

export default FriendsPage;
